import React, { useEffect, useMemo, useState } from "react";
import { Combobox } from "@headlessui/react";
import { useDebounce } from "use-debounce";
import { IIndustryAlias, IIndustryAliasSearchResult } from "../../../types/types";
import { searchIndustryAliases } from "../../../utils/server/industry";
import { checkIfTypos } from '../../../shared/utils';

type Props = {
  industryId?: number;
  initialAlias?: IIndustryAlias | null;
  onAliasSelected: (aliasObj: IIndustryAlias) => void;
};

type CreateOption = {
  __create__: true;
  id: number;
  alias: string;
  industry_id: number;
  match_type: "create";
  score: number;
  spelling: string[];
};

type AliasOption = IIndustryAliasSearchResult | CreateOption;

const IndustryAliasSelect: React.FC<Props> = ({ industryId, initialAlias, onAliasSelected }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery] = useDebounce(query, 300);
  const [results, setResults] = useState<IIndustryAliasSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<AliasOption | null>(null);
  const [spelling, setSpelling] = useState<string[]>([]);

  const topScore = useMemo(() => {
    if (!results?.length) return 0;
    return results.reduce((max, r) => Math.max(max, r?.score ?? (r.match_type === 'exact' ? 1 : 0)), 0);
  }, [results]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!industryId || !debouncedQuery.trim()) {
        setResults([]);
        setSpelling([]);
        return;
      }
      setLoading(true);
      try {
        // Spellcheck in parallel with search
        const spellPromise = checkIfTypos(debouncedQuery.trim());
        const res = await searchIndustryAliases({
          industry_id: industryId,
          q: debouncedQuery.trim(),
          mode: "auto",
          threshold: 0.0, // request all, we decide client-side
          limit: 25,
          page: 1,
        });
        const spellRes = await spellPromise;
        if (cancelled) return;
        setResults((res.results as IIndustryAliasSearchResult[]) || []);
        setSpelling(spellRes || []);
      } catch (e) {
        if (!cancelled) {
          setResults([]);
          setSpelling([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, industryId]);

  // Prefill selection when initialAlias provided and matches industry
  useEffect(() => {
    if (!initialAlias || !industryId) return;
    if (initialAlias.industry !== industryId) return;
    setSelected({ id: (initialAlias.id ?? -1) as number, alias: initialAlias.alias, industry_id: industryId, match_type: initialAlias.id ? 'exact' : 'create', score: initialAlias.id ? 1 : 0 });
  }, [initialAlias, industryId]);

  function handleSelect(option: AliasOption) {
    setSelected(option);
    if (!option) return;
    if ((option as CreateOption).__create__) {
      const createOpt = option as CreateOption;
      onAliasSelected({ alias: createOpt.alias, industry: industryId as number, id: createOpt.id ?? 0 });
    } else {
      const found = option as IIndustryAliasSearchResult;
      onAliasSelected({ id: found.id, alias: found.alias, industry: found.industry_id });
    }
  }

  const showCreate = !!industryId && !!query.trim() && topScore < 0.85;
  const options: AliasOption[] = useMemo(() => {
    const base: IIndustryAliasSearchResult[] = results || [];
    if (showCreate) {
      // Emulate a create option
      return [
        { __create__: true, id: -1, alias: query.trim(), industry_id: industryId as number, match_type: "create", score: 0, spelling },
        ...base,
      ];
    }
    return base;
  }, [results, showCreate, query, industryId]);

  function isCreateOption(opt: AliasOption): opt is CreateOption {
    return (opt as CreateOption).__create__ === true;
  }

  return (
    <Combobox<AliasOption> value={selected} onChange={handleSelect} nullable>
      <Combobox.Label className="block text-sm font-medium text-gray-700 text-left w-full">Industry Alias</Combobox.Label>
      {industryId && (
        <div className="mt-1 mb-1" aria-live="polite">
          {!!debouncedQuery.trim() && (
            spelling.length > 0 ? (
              <div className="flex items-start gap-2 text-red-700 text-xs">
                <div>
                  <div className="font-medium">Possible misspellings detected</div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {spelling.slice(0, 6).map((w) => (
                      <span key={w} className="inline-block bg-red-100 text-red-800 px-2 py-0.5 rounded-full">{w}</span>
                    ))}
                    {spelling.length > 6 && (
                      <span className="inline-block text-red-600">+{spelling.length - 6} more</span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-xs">Spelling looks good</div>
            )
          )}
        </div>
      )}
      <Combobox.Input
        className={`mt-1 w-full border rounded-md px-3 py-2 ${!industryId ? 'opacity-60 cursor-not-allowed' : ''}`}
        onChange={(e) => setQuery(e.target.value)}
        displayValue={(opt: AliasOption | null) => (opt ? (isCreateOption(opt) ? opt.alias : opt.alias) : "")}
        placeholder={industryId ? "Search or create alias…" : "Select an industry first"}
        readOnly={!industryId}
      />
      <Combobox.Options className="mt-1 max-h-60 overflow-auto border rounded-md bg-white">
        {loading && <div className="px-4 py-2 text-gray-500">Searching…</div>}
        {!loading && options.length === 0 && (
          <div className="px-4 py-2 text-gray-500">No results</div>
        )}
        {options.map((opt: AliasOption) => (
          <Combobox.Option key={`${isCreateOption(opt) ? 'create' : (opt as IIndustryAliasSearchResult).id}-${opt.alias}`} value={opt} className={({ active }) => `cursor-pointer select-none px-4 py-2 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'}`}>
            {isCreateOption(opt) ? (
              <div className="flex items-center justify-between w-full">
                <span> Create “{opt.alias}” </span>
                <span className="text-xs">score &lt; 0.85</span>
              </div>
            ) : (
              <div className="flex items-center justify-between w-full">
                <span>{(opt as IIndustryAliasSearchResult).alias}</span>
                <span className="text-xs text-gray-500">{(opt as IIndustryAliasSearchResult).match_type}{typeof (opt as IIndustryAliasSearchResult).score === 'number' ? ` (${((opt as IIndustryAliasSearchResult).score as number).toFixed(2)})` : ''}</span>
              </div>
            )}
          </Combobox.Option>
        ))}
      </Combobox.Options>
    </Combobox>
  );
};

export default IndustryAliasSelect;


